import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, Button, Collapse } from 'reactstrap';
import { CoreExportModuleSchoolUnit, CoreImportModuleSchoolUnit, CoreModuleView, ModuleType } from '../../../models/CoreModels';
import { ExportSchoolUnitCheckbox, ImportSchoolUnitCheckbox } from './schoolUnit/SchoolUnitInput';
import { useTranslation } from 'react-i18next'


interface SchoolUnitConfigurationProps {
    schoolUnits: CoreExportModuleSchoolUnit[] | CoreImportModuleSchoolUnit[]
    moduleType: ModuleType
    disabled: boolean
    mergeUpdate: (name: keyof CoreModuleView, arg: CoreExportModuleSchoolUnit[] | CoreImportModuleSchoolUnit[]) => void
    mergedSchoolUnits: CoreExportModuleSchoolUnit[] | CoreImportModuleSchoolUnit[]
    selectSchoolUnits: boolean
    updateValidation: (arg: boolean) => void
    warnings: (arg: string[]) => void
}

function SchoolUnitsConfiguration(props: SchoolUnitConfigurationProps) {

    if (!props.selectSchoolUnits) {
        return null;
    }

    switch (props.moduleType) {
        case (ModuleType.ExportModule):
            return (
                <DisplaySchoolUnits<CoreExportModuleSchoolUnit>
                    moduleType={ModuleType.ExportModule}
                    schoolUnits={props.schoolUnits}
                    mergedSchoolUnits={props.mergedSchoolUnits}
                    warnings={(warning) => props.warnings(warning)}
                    invalid={(valid) => props.updateValidation(valid)}
                    mergeUpdate={(mergedSchoolUnits) => props.mergeUpdate('exportSchoolUnits', mergedSchoolUnits)}
                    disabled={props.disabled}
                />);
        case (ModuleType.ImportModule):
            return (
                <DisplaySchoolUnits<CoreImportModuleSchoolUnit>
                    moduleType={ModuleType.ImportModule}
                    schoolUnits={props.schoolUnits}
                    mergedSchoolUnits={props.mergedSchoolUnits}
                    warnings={(warning) => props.warnings(warning)}
                    invalid={(valid) => props.updateValidation(valid)}
                    mergeUpdate={(mergedSchoolUnits) => props.mergeUpdate('importSchoolUnits', mergedSchoolUnits)}
                    disabled={props.disabled}
                />);
        default: return null;
    };
};

interface DisplaySchoolUnitsProps<TModuleSchoolUnit extends CoreExportModuleSchoolUnit | CoreImportModuleSchoolUnit> {
    moduleType: ModuleType
    schoolUnits: TModuleSchoolUnit[]
    mergedSchoolUnits: TModuleSchoolUnit[]
    disabled: boolean
    mergeUpdate: (arg0: TModuleSchoolUnit[]) => void
    invalid: (validation: boolean) => void
    warnings: (warnings: string[]) => void
};

function DisplaySchoolUnits<TModuleSchoolUnit extends CoreExportModuleSchoolUnit | CoreImportModuleSchoolUnit>(props: DisplaySchoolUnitsProps<TModuleSchoolUnit>) {
    const { t } = useTranslation('configuration');

    const [open, setOpen] = useState<boolean>(true);

    useEffect(() => {
        props.invalid(validate(props.mergedSchoolUnits).length ? true : false)
    }, []);

    const renderCheckbox = (schoolUnit: TModuleSchoolUnit) => {
        switch (props.moduleType) {
            case ModuleType.ExportModule:
                return <ExportSchoolUnitCheckbox
                    schoolUnit={schoolUnit}
                    disabled={props.disabled}
                    onChange={(name, schoolUnit) => updateProperty(schoolUnit as TModuleSchoolUnit)}
                    validation={validate(props.mergedSchoolUnits)}
                />;
            case ModuleType.ImportModule:
                return <ImportSchoolUnitCheckbox
                    disabled={props.disabled}
                    schoolUnit={schoolUnit}
                    onChange={(name, schoolUnit) => updateProperty(schoolUnit as TModuleSchoolUnit)}
                    validation={validate(props.mergedSchoolUnits)}
                />;
            default: return t('schoolUnitConfiguration.unknownModuleType', { moduleType: props.moduleType });
        };
    };

    const validate = (schoolUnits: TModuleSchoolUnit[]) => {
        const validation: string[] = [];
        schoolUnits.forEach(su => {
            if (!su.isActive && su.shouldImport) {
                validation.push(`inactive-${su.id}`);
            }
        });

        if (props.moduleType === ModuleType.ImportModule) {
            const importSchools = schoolUnits as CoreImportModuleSchoolUnit[]
            importSchools.forEach(su => {
                if (!su.shouldImport && su?.exportModules && su.exportModules.length >= 1) {
                    validation.push(`references-${su.id}`);
                }
            })
        };

        return validation;
    };

    const warnings = (mergedSchoolUnits: TModuleSchoolUnit[]) => {
        const warningMessages: string[] = [];

        mergedSchoolUnits.forEach(msu => {
            const configurationSchoolUnit = props.schoolUnits.find(su => su.id === msu.id)
            if (!msu.shouldImport && msu.isActive && configurationSchoolUnit?.shouldImport) {
                warningMessages.push(t('schoolUnitConfiguration.removeWarning'));
            }
        });

        return warningMessages.filter((value, index, array) => array.indexOf(value) === index);
    };

    const updateProperty = (schoolUnit: TModuleSchoolUnit) => {
        const index = props.mergedSchoolUnits.findIndex(s => schoolUnit.id === s.id);
        const newUpdates = [...props.mergedSchoolUnits.slice(0, index), schoolUnit, ...props.mergedSchoolUnits.slice(index + 1)]

        props.warnings(warnings(newUpdates))

        updateData(newUpdates);
    };

    const updateData = (merged: TModuleSchoolUnit[]) => {
        props.mergeUpdate(merged);

        props.invalid(validate(merged).length ? true : false)
    };

    if (props.mergedSchoolUnits && props.mergedSchoolUnits.length > 0) {
        return <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                {t('schoolUnitConfiguration.cardHeader')}
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>
                    <ul className="schoolunit_list">
                        {(props.mergedSchoolUnits).map(schoolUnit =>
                            <li key={schoolUnit.id}>
                                {renderCheckbox(schoolUnit)}
                            </li>
                        )}
                    </ul>
                </CardBody>
            </Collapse>
        </Card>
    };

    return null;
};

export default SchoolUnitsConfiguration;
